import type { Session, Role, CognitoRole } from "../types";
import { limpiarEstadoDeSesion } from "./estadoDeSesion";
import {
  signIn as cognitoSignIn, signOut as cognitoSignOut, refreshIdToken as refreshToken,
  type SignInResult,
} from "./cognito";

const KEY = "ikids.session";
const TOKEN_KEY = "ikids.idToken";

/*
 * El modo `demo` —contraseñas en el bundle, sesión falsificable desde la consola—
 * SE ELIMINÓ al ir a producción, junto con `accounts.ts`. Ya no hay interruptor:
 * la autenticación es Cognito y punto. Un flag que puede apagar la seguridad es un
 * flag que alguien acabará apagando.
 */

/**
 * Mapea el rol de Cognito al Role del frontend (3): quién ve el panel, quién las
 * salas de un participante y quién las fijas del kiosco (`SALAS_KIOSCO`).
 *
 * EL PARÁMETRO ES `CognitoRole`, NO `string`, y eso es el arreglo.
 *
 * Antes aceptaba cualquier cadena y devolvía "librarian" para todo lo que no fuera
 * "participant". O sea: un rol que esta versión no conociera no se quedaba fuera,
 * entraba como PERSONAL. Y "librarian" no es una etiqueta cosmética —
 * `access.tsx` le entrega `null` a AccessGate, que significa TODAS las salas.
 *
 * Con el tipo estrecho, quien quiera llamar a esto tiene que haber comprobado el
 * rol antes: el compilador no deja saltarse la guarda de `persist`.
 */
function mapRole(cognitoRole: CognitoRole): Role {
  if (cognitoRole === "participant") return "student";
  if (cognitoRole === "kiosk") return "kiosk";
  return "librarian";
}

// --- Modo Cognito --------------------------------------------------------------
export type CognitoLoginResult =
  | { kind: "ok"; session: Session }
  | { kind: "newPasswordRequired"; complete: (newPassword: string) => Promise<CognitoLoginResult> }
  | { kind: "resetRequired" };

/*
 * Los roles que esta versión de la web reconoce. Un `custom:role` que no esté
 * aquí deja `cognitoRole` en `undefined`, y entonces `navFor` cae al menú de
 * bibliotecaria: la persona entra, ve un menú que no es el suyo y las pantallas
 * fallan o desaparecen sin decir por qué.
 *
 * O sea: AÑADIR UN ROL AL BACKEND Y NO A ESTA LISTA ES UN FALLO DE INICIO DE
 * SESIÓN, no un detalle de tipos. Pasó con `agent_admin`.
 */
const COGNITO_ROLES: readonly CognitoRole[] = [
  "super_admin", "admin", "agent_admin", "librarian", "participant", "kiosk",
];

function persist(idToken: string, claims: Record<string, any>): Session {
  const raw = String(claims["custom:role"] ?? "");
  /*
   * SIN ROL CONOCIDO NO SE ABRE SESIÓN. Falla cerrado, y es deliberado que sea
   * aquí: es el único sitio por el que se instala una sesión.
   *
   * No se pierde nada por rechazar: el backend ya responde 403 a estas cuentas
   * («la cuenta no tiene un rol asignado o no está activa», auth.ts). Antes la web
   * dejaba entrar igual y ese 403 aparecía después, traducido a un síntoma
   * distinto en cada pantalla — «no hay residenciales en tu alcance» y demás.
   */
  if (!COGNITO_ROLES.includes(raw as CognitoRole)) {
    throw new Error(
      "Tu cuenta no tiene un rol válido en esta versión de la plataforma. " +
        "Avisa a quien la administra."
    );
  }
  const cognitoRole = raw as CognitoRole;
  const session: Session = {
    user: String(claims["cognito:username"] ?? claims.email ?? claims.sub ?? "usuario"),
    role: mapRole(cognitoRole),
    cognitoRole,
  };
  /*
   * Olvidar lo del usuario anterior ANTES de instalar la sesión nueva.
   *
   * También aquí y no solo al salir: si alguien cerró el navegador sin cerrar
   * sesión, el `logout` nunca corrió. Y en un kiosco de biblioteca eso es lo
   * normal, no la excepción.
   */
  limpiarEstadoDeSesion();
  // Sesión nueva, aviso nuevo: el «tu sesión ya no es válida» era de la anterior.
  expirada = false;
  sessionStorage.setItem(KEY, JSON.stringify(session));
  sessionStorage.setItem(TOKEN_KEY, idToken);
  return session;
}

function wrap(r: SignInResult): CognitoLoginResult {
  if (r.kind === "ok") return { kind: "ok", session: persist(r.idToken, r.claims) };
  if (r.kind === "resetRequired") return r;
  return { kind: "newPasswordRequired", complete: async (pw) => wrap(await r.complete(pw)) };
}

export async function loginCognito(user: string, password: string): Promise<CognitoLoginResult> {
  return wrap(await cognitoSignIn(user.trim(), password));
}

/*
 * Aquí estaba `getIdToken()`, que devolvía el token cacheado sin mirar si seguía
 * vivo. Era lo que usaba `apiFetch`, y por eso la app dejaba de funcionar a los 60
 * minutos. Lo sustituye `getValidIdToken()`, que renueva. Se eliminó en vez de
 * dejarlo "por si acaso": el que quede a mano es el que alguien volverá a usar.
 */

/** Payload del JWT, o null si no se puede leer. */
function payloadDe(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/** `exp` del JWT en segundos, o null si no se puede leer. */
function expiraEn(token: string): number | null {
  const exp = payloadDe(token)?.exp;
  return typeof exp === "number" ? exp : null;
}

/**
 * ¿El token renovado es de la MISMA persona que el que tenía esta pestaña?
 *
 * Amplify ya guarda sus tokens por pestaña (lib/cognito.ts), así que esto no
 * debería fallar nunca. Se comprueba igual porque el fallo que evita es el peor
 * posible: con los tokens compartidos entre pestañas, la renovación entregaba el
 * token de la última cuenta que había entrado en OTRA pestaña, y esta seguía
 * enseñando el nombre de una persona mientras actuaba como otra. Si vuelve a
 * pasar por cualquier camino, se trata como sesión caída: nunca se usa ese token.
 *
 * `sub` es el id inmutable de Cognito; `cognito:username` es el respaldo por si
 * algún token viniera sin él. Si no hay con qué comparar, no se acepta.
 */
function mismaPersona(anterior: string, nuevo: string): boolean {
  const a = payloadDe(anterior);
  const b = payloadDe(nuevo);
  if (!a || !b) return false;
  if (a.sub && b.sub) return a.sub === b.sub;
  const ua = a["cognito:username"];
  return !!ua && ua === b["cognito:username"];
}

/*
 * Pide un token renovado y lo instala SOLO si sirve. Es el único sitio por el que
 * entra un token renovado, y por eso también el único que decide «la sesión cayó»:
 * antes `apiFetch` devolvía el 401 sin avisar cuando la renovación fallaba de
 * entrada, y el participante se quedaba en «No pudimos verificar tu acceso» sin
 * botón para salir.
 */
async function renovar(anterior: string, forzar: boolean): Promise<string | null> {
  const nuevo = await refreshToken(forzar);
  if (!nuevo) {
    // Sin red la renovación también falla, pero eso no es una sesión caída: volver
    // a entrar tampoco funcionaría, y el aviso mandaría a la persona al sitio
    // equivocado. Se marca solo si hay conexión.
    if (navigator.onLine !== false) marcarSesionExpirada();
    return null;
  }
  if (!mismaPersona(anterior, nuevo)) {
    console.warn("[ikids] el token renovado es de otra cuenta; se descarta.");
    marcarSesionExpirada();
    return null;
  }
  sessionStorage.setItem(TOKEN_KEY, nuevo);
  return nuevo;
}

/**
 * Un id token que de verdad sirve, renovándolo si está por vencer.
 *
 * Los de Cognito duran 60 minutos. Sin esto, la app funcionaba una hora y luego
 * TODO devolvía 401 — y como cada pantalla traducía el fallo a su manera, el
 * síntoma era "no hay residenciales en tu alcance" en vez de "vuelve a entrar".
 * A una bibliotecaria le pasaría cada hora, a media jornada.
 *
 * El margen de 2 minutos no es paranoia: un token que vence en diez segundos llega
 * vencido si la petición tarda, y el reloj del kiosco no tiene por qué estar en hora.
 */
export async function getValidIdToken(): Promise<string | null> {
  const actual = sessionStorage.getItem(TOKEN_KEY);
  if (!actual) return null;
  const exp = expiraEn(actual);
  const ahora = Math.floor(Date.now() / 1000);
  if (exp && exp - ahora > 120) return actual;
  return renovar(actual, false);
}

/**
 * Fuerza una renovación (tras un 401 inesperado). Null = la sesión murió, y ya
 * queda marcada como caída.
 *
 * `forceRefresh`: sin él, Amplify devuelve el token cacheado si le parece vigente
 * —el mismo que el servidor acaba de rechazar— y el reintento es un 401 seguro.
 */
export async function refrescarToken(): Promise<string | null> {
  const actual = sessionStorage.getItem(TOKEN_KEY);
  if (!actual) return null;
  return renovar(actual, true);
}

const EXPIRADA_KEY = "ikids.sesionExpirada";

/*
 * Sesión no renovable: se AVISA, no se expulsa.
 *
 * La primera versión hacía `logout()` + `location.reload()` en cuanto una petición
 * daba 401 irrecuperable. Suena correcto y en la práctica fue mucho peor: una
 * llamada de fondo fallida echaba de la plataforma a alguien que estaba a mitad de
 * un registro, sin decirle por qué y sin dejarle leer el error. Y como cualquier
 * pantalla dispara peticiones al abrirse, entrar a una sección se convertía en
 * "me saca".
 *
 * Ahora se marca la sesión como caída y la UI enseña un aviso con un botón. El
 * usuario decide cuándo re-entrar, y mientras tanto puede ver qué falló.
 */
let expirada = false;
const oyentes = new Set<() => void>();

export function marcarSesionExpirada(): void {
  if (expirada) return; // varias peticiones en paralelo no deben avisar N veces
  expirada = true;
  oyentes.forEach((f) => f());
}

export function sesionEstaExpirada(): boolean {
  return expirada;
}

/** Suscribe a la caída de sesión. Devuelve la baja. */
export function alExpirarSesion(f: () => void): () => void {
  oyentes.add(f);
  return () => oyentes.delete(f);
}

/** Lo llama el botón del aviso: ahora sí, limpiar y volver al login. */
export function volverAlLogin(): void {
  logout();
  sessionStorage.setItem(EXPIRADA_KEY, "1");
  location.hash = "#/login";
  location.reload();
}

/** ¿Se llegó al login por expiración? Lo consume Login para decirlo (y lo borra). */
export function veniaDeExpirar(): boolean {
  const si = sessionStorage.getItem(EXPIRADA_KEY) === "1";
  if (si) sessionStorage.removeItem(EXPIRADA_KEY);
  return si;
}

// --- Común ---------------------------------------------------------------------
export function getSession(): Session | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function logout(): void {
  sessionStorage.removeItem(KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  /*
   * El aviso de sesión caída es de ESTA sesión. Sin esto, quien entraba después
   * en la misma pestaña —la app no recarga al salir— empezaba ya con la franja
   * roja de «tu sesión ya no es válida».
   */
  expirada = false;
  /*
   * Y lo que guardan los módulos en memoria, que el sessionStorage no cubre: la
   * aplicación no recarga la página al salir, así que esas cachés seguirían
   * vivas para quien entre después. Ver lib/estadoDeSesion.ts.
   */
  limpiarEstadoDeSesion();
  try {
    cognitoSignOut();
  } catch {
    /* la librería puede no tener sesión cacheada; no es un error */
  }
}
